import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Switch, Chip, IconButton, LinearProgress, Checkbox, Button, Toolbar, Grid, Divider
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_REPORTS = [
	{
		name: 'Spain - Malaga Airport Weekly',
		searches: 384,
		sites: ['rentalspot.demo', 'pricefly.demo'],
		hours: ['08:00', '14:00'],
		frequency: ['Mondays', 'Wednesdays', 'Fridays'],
		pickup: 'Malaga Airport (AGP)',
		dropoff: null,
		date: '10 Feb 2025 08:15',
		status: 'ready',
		enabled: true,
		hasAnalyzers: true,
		selected: true,
	},
	{
		name: 'UK - Heathrow Daily Monitor',
		searches: 1250,
		sites: ['bookride.demo', 'findwheels.demo', 'autolink.demo'],
		hours: ['06:00', '12:00', '18:00'],
		frequency: ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays'],
		pickup: 'Heathrow Airport (LHR)',
		dropoff: null,
		date: null,
		status: 'running',
		progress: 67,
		completeTasks: '838',
		totalTasks: '1250',
		enabled: true,
		hasAnalyzers: false,
	},
	{
		name: 'France - Paris CDG Brokers',
		searches: 192,
		sites: ['rentalspot.demo'],
		hours: ['10:00'],
		frequency: ['Tuesdays', 'Thursdays'],
		pickup: 'Paris CDG Airport (CDG)',
		dropoff: 'Paris Orly (ORY)',
		date: '08 Feb 2025 10:22',
		status: 'ready',
		enabled: true,
		hasAnalyzers: true,
	},
	{
		name: 'Italy - Rome FCO Rating',
		searches: 96,
		sites: ['pricefly.demo', 'bookride.demo'],
		hours: ['09:00'],
		frequency: ['Mondays'],
		pickup: 'Rome Fiumicino (FCO)',
		dropoff: null,
		date: '03 Feb 2025 09:10',
		status: 'stopped',
		enabled: false,
		hasAnalyzers: false,
	},
	{
		name: 'Germany - Munich MUC Full',
		searches: 576,
		sites: ['rentalspot.demo', 'pricefly.demo', 'findwheels.demo'],
		hours: ['07:00', '13:00', '19:00'],
		frequency: ['Mondays', 'Wednesdays', 'Fridays', 'Sundays'],
		pickup: 'Munich Airport (MUC)',
		dropoff: null,
		date: '10 Feb 2025 19:03',
		status: 'ready',
		enabled: true,
		hasAnalyzers: true,
	},
]

const MULTI_BULK_ACTIONS = [
	{ icon: 'globe-pointer',  tooltip: 'Edit Sites for Multiple Reports',     tourId: 'tour-multi-btn-sites'      },
	{ icon: 'calendars',      tooltip: 'Edit Pick ups for Multiple Reports',  tourId: 'tour-multi-btn-pickups'    },
	{ icon: 'envelopes',      tooltip: 'Edit Emails for Multiple Reports',    tourId: 'tour-multi-btn-emails'     },
	{ icon: 'calendar-clock', tooltip: 'Edit Frequency for Multiple Reports', tourId: 'tour-multi-btn-frequency'  },
	{ icon: 'gears',          tooltip: 'Perform multiple executions',         tourId: 'tour-multi-btn-perform'    },
	{ icon: 'circle-stop',    tooltip: 'Stop multiple executions',            tourId: 'tour-multi-btn-stop'       },
	{ icon: 'copy',           tooltip: 'Copy Multiple Reports',               tourId: 'tour-multi-btn-copy'       },
	{ icon: 'trash',          tooltip: 'Delete Multiple Reports',             tourId: 'tour-multi-btn-delete'     },
	{ icon: 'toggle-off',     tooltip: 'Disable Multiple Reports',            tourId: 'tour-multi-btn-disable'    },
	{ icon: 'toggle-on',      tooltip: 'Enable Multiple Reports',             tourId: 'tour-multi-btn-enable'     },
]

const AnalyzerIcon = ({ icon, color, title, tourId }) => (
	<IconButton size="small" title={title} data-tour-id={tourId} sx={{ p: 0.3 }}>
		<IconSeeker icon={icon} size="xs" style={{ color }} />
	</IconButton>
)

const ActionButton = ({ icon, tooltip }) => (
	<IconButton size="small" title={tooltip} sx={{ p: 0.5 }}>
		<IconSeeker icon={icon} size="xs" />
	</IconButton>
)

const MultiToolbar = () => (
	<Toolbar
		data-tour-id="tour-multi-toolbar"
		variant="dense"
		sx={{ px: 2, minHeight: 44, bgcolor: 'primary.dark', color: 'white', gap: 1, flexWrap: 'wrap' }}
	>
		<Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}>
			1 selected:
		</Typography>
		<Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
			{MULTI_BULK_ACTIONS.map(({ icon, tooltip, tourId }) => (
				<IconButton key={tooltip} size="small" title={tooltip} data-tour-id={tourId} disabled sx={{ p: 0.4 }}>
					<IconSeeker icon={icon} size="xs" style={{ color: 'rgba(255,255,255,0.7)' }} />
				</IconButton>
			))}
		</Box>
		<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
			* Some buttons vary by report state
		</Typography>
	</Toolbar>
)


export default function ReportsDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader
					title={
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="subtitle1" fontWeight={600}>Reports Common List</Typography>
							<Button
								size="small"
								variant="contained"
								startIcon={<IconSeeker icon="file-plus" size="xs" />}
								disabled
								sx={{ textTransform: 'none' }}
							>
								Create Report
							</Button>
						</Box>
					}
				/>

				<MultiToolbar />
				<Divider />

				<Toolbar variant="dense" sx={{ px: 2, minHeight: 40 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="caption" color="text.secondary">
							{DEMO_REPORTS.length} reports
						</Typography>
					</Box>
				</Toolbar>

				<Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' } }}>
								<TableCell padding="checkbox"><Checkbox size="small" disabled /></TableCell>
								<TableCell>Report name</TableCell>
								<TableCell align="center">Search numbers</TableCell>
								<TableCell>Sites</TableCell>
								<TableCell>Frequency Hours</TableCell>
								<TableCell>Frequency</TableCell>
								<TableCell>Pick up & Drop off</TableCell>
								<TableCell align="center">Date</TableCell>
								<TableCell align="center">Off/On</TableCell>
								<TableCell align="center">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_REPORTS.map(r => (
								<TableRow key={r.name} sx={{ '& td': { py: 1.5, fontSize: '0.8rem' }, ...(r.selected && { bgcolor: 'action.selected' }) }}>
									<TableCell padding="checkbox"><Checkbox size="small" checked={!!r.selected} disabled /></TableCell>

									<TableCell sx={{ fontWeight: 500, maxWidth: 200 }}>
										<Typography variant="body2" fontWeight={500} noWrap>{r.name}</Typography>
									</TableCell>

									<TableCell align="center">
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
											<Typography variant="body2">{r.searches.toLocaleString()}</Typography>
											{r.searches >= 1000 && <IconSeeker icon="fire" size="xs" style={{ color: '#ef5350' }} />}
										</Box>
									</TableCell>

									<TableCell>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
											{r.sites.map(s => (
												<Chip key={s} label={s.replace('.com', '')} size="small"
													sx={{ height: 20, fontSize: '0.65rem' }} />
											))}
										</Box>
									</TableCell>

									<TableCell>
										<Typography variant="caption">{r.hours.join(', ')}</Typography>
									</TableCell>

									<TableCell>
										<Typography variant="caption">
											{r.frequency.map(f => f.slice(0, 3)).join(', ')}
										</Typography>
									</TableCell>

									<TableCell>
										<Typography variant="caption" sx={{ display: 'block' }}>{r.pickup}</Typography>
										{r.dropoff && <Typography variant="caption" color="text.secondary">→ {r.dropoff}</Typography>}
									</TableCell>

									<TableCell align="center" sx={{ minWidth: 130 }}>
										{r.status === 'ready' && <Box>
											<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{r.date}</Typography>
											<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>
												{r.hasAnalyzers && <>
													<AnalyzerIcon icon="tally" color="#333" title="LOR Analyzer" tourId="tour-date-btn-lor" />
													<AnalyzerIcon icon="calendar-days" color="#1976d2" title="DOR Analyzer" tourId="tour-date-btn-dor" />
													<AnalyzerIcon icon="handshake" color="#ed6c02" title="Brokers Analyzer" tourId="tour-date-btn-brokers" />
													<AnalyzerIcon icon="chart-line" color="#2e7d32" title="Direct Site Analyzer" tourId="tour-date-btn-direct" />
													<AnalyzerIcon icon="star" color="#ed6c02" title="Rating Analyzer" tourId="tour-date-btn-rating" />
													<AnalyzerIcon icon="clock-rotate-left" color="#555" title="View in real time historical" tourId="tour-date-btn-realtime" />
													<AnalyzerIcon icon="file-excel" color="#e65100" title="Get custom categories matches" tourId="tour-date-btn-categories" />
													<AnalyzerIcon icon="file-excel" color="#00695c" title="Get custom acriss matches" tourId="tour-date-btn-acriss" />
												</>}
												<AnalyzerIcon icon="cloud-arrow-down" color="#c2185b" title="Download multi report" tourId="tour-date-btn-multireport" />
												<AnalyzerIcon icon="file-arrow-down" color="#4527a0" title="Download multi export" tourId="tour-date-btn-multiexport" />
											</Box>
										</Box>}
										{r.status === 'running' && <Box>
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
												<Typography variant="caption" color="primary" fontWeight={500}>Running</Typography>
												<Typography variant="caption" color="text.secondary">
													{r.completeTasks}/{r.totalTasks}
												</Typography>
											</Box>
											<LinearProgress
												data-tour-id="tour-running-block"
												variant="determinate"
												value={r.progress}
												sx={{ height: 6, borderRadius: 1, cursor: 'pointer' }}
											/>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.3, display: 'block' }}>
												Click progress bar to view details
											</Typography>
										</Box>}
										{r.status === 'stopped' && <Box>
											<Typography variant="caption" color="text.secondary">{r.date}</Typography>
											<Typography variant="caption" color="error" sx={{ display: 'block' }}>Stopped</Typography>
										</Box>}
										{!r.date && r.status !== 'running' && (
											<Typography variant="caption" color="text.secondary">Not executed</Typography>
										)}
									</TableCell>

									<TableCell align="center">
										<Switch size="small" checked={r.enabled} disabled />
									</TableCell>

									<TableCell align="center">
										<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.2 }}>
											<ActionButton icon="gears" tooltip="Perform" />
											<ActionButton icon="pen" tooltip="Edit" />
											<ActionButton icon="clone" tooltip="Clone & Edit" />
											<ActionButton icon="circle-info" tooltip="Show Report Info" />
											{r.status === 'running' && (
												<Box data-tour-id="tour-force-action">
													<ActionButton icon="circle-stop" tooltip="Stop Execution" />
												</Box>
											)}
											<ActionButton icon="trash-can" tooltip="Delete" />
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>
	</Grid>
}
